import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useTravelDurations} from 'src/components/clinic/booking/booking-health-declaration/hooks/useTravelDurations';
import {TravelModal} from 'src/components/clinic/booking/booking-health-declaration/TravelModal';
import {EditIcon} from 'src/components/icons/EditIcon';
import {PlusIcon} from 'src/components/icons/PlusIcon';
import {Spinner} from 'src/components/layout/loader/Spinner';
import ModalPortal from 'src/components/layout/modal/ModalPortal';
import {DataActions, useArrivalContext} from 'src/context/ArrivalContext';

export function DropinTravel() {
    const {t} = useTranslation();
    const {travelDurations} = useTravelDurations();
    const {
        dataState: {trip, tripDepartureDate, countries},
        dispatch
    } = useArrivalContext();
    const [modalStatus, setModalStatus] = useState<'closed' | 'travel'>('closed');
    const [showWarning, setShowWarning] = useState(false);
    const [answersChanged, setAnswersChanged] = useState(false);

    const hasTravelInformation = trip !== undefined && tripDepartureDate !== null;

    const travelSubheading = hasTravelInformation
        ? t('healthdeclaration.destination_edit_section_header')
        : t('healthdeclaration.destination_add_section_header');
    const travelLinkText = hasTravelInformation
        ? t('healthdeclaration.destination_edit_section_link_text')
        : t('healthdeclaration.destination_add_section_link_text');

    return (
        <div className='arrival-dropin-hd__travel'>
            <h2 className='health-declaration__sub-heading'>{travelSubheading}</h2>
            {hasTravelInformation && (
                <ul className='health-declaration__destination-list'>
                    {trip.map((destination, index) => {
                        const countryName = countries.find(({id}) => destination.countryId === id)?.name;
                        const durationLabel = travelDurations.find(({id}) => destination.duration === id)?.label;

                        return (
                            <li key={index}>
                                {t('healthdeclaration.destination_list_item', {
                                    destination: countryName,
                                    duration: durationLabel
                                })}
                            </li>
                        );
                    })}
                </ul>
            )}
            <p>
                <a
                    href='/'
                    onClick={(e) => {
                        e.preventDefault();
                        setModalStatus('travel');
                    }}
                    className='health-declaration__add-travel-link'
                >
                    {hasTravelInformation ? <EditIcon /> : <PlusIcon />}
                    {travelLinkText}
                </a>
            </p>

            {modalStatus === 'travel' && (
                <ModalPortal
                    onCloseClick={() => {
                        if (answersChanged) {
                            setShowWarning(true);
                            return;
                        }
                        setModalStatus('closed');
                    }}
                >
                    {showWarning && (
                        <ModalPortal
                            primaryActionLabel={t('button_yes.text')}
                            onPrimaryAction={() => {
                                setShowWarning(false);
                                setAnswersChanged(false);
                                setModalStatus('closed');
                            }}
                            secondaryActionLabel={t('button_no.text')}
                            onSecondaryAction={() => setShowWarning(false)}
                        >
                            <p>
                                <b>{t('healthdeclaration.close_warning_header')}</b>
                                <br />
                                {t('healthdeclaration.close_warning_content')}
                            </p>
                        </ModalPortal>
                    )}
                    <TravelModal
                        countries={countries}
                        initialDestinations={trip}
                        initialDepartureDate={tripDepartureDate}
                        onChange={(isChanged) => setAnswersChanged(isChanged)}
                        onSaveClick={({departureDate, destinations}) => {
                            dispatch({
                                type: DataActions.SET_TRIP,
                                value: {trip: destinations, tripDepartureDate: departureDate}
                            });
                            setModalStatus('closed');
                        }}
                    />
                </ModalPortal>
            )}
        </div>
    );
}
